import { useState, useCallback } from 'react'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { apiRequest } from '@/lib/api'

// ===================
// Types
// ===================

interface MessageFeedbackProps {
  messageId: string
  sessionId: string
}

type Rating = 'positive' | 'negative' | null

// ===================
// MessageFeedback Component
// ===================

export function MessageFeedback({ messageId, sessionId }: MessageFeedbackProps) {
  const [rating, setRating] = useState<Rating>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleRate = useCallback(async (newRating: 'positive' | 'negative') => {
    if (isSubmitting) return

    const previousRating = rating

    // Toggle off if clicking same rating
    if (rating === newRating) {
      setRating(null)
      setIsSubmitting(true)
      try {
        await apiRequest(`/analytics/feedback/${messageId}`, { method: 'DELETE' })
      } catch {
        // Revert on failure
        setRating(previousRating)
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    // Optimistic update
    setRating(newRating)
    setIsSubmitting(true)
    try {
      await apiRequest('/analytics/feedback', {
        method: 'POST',
        body: {
          sessionId,
          messageId,
          rating: newRating,
        },
      })
    } catch {
      // Revert on failure
      setRating(previousRating)
    } finally {
      setIsSubmitting(false)
    }
  }, [messageId, sessionId, rating, isSubmitting])

  return (
    <div className="inline-flex items-center gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-6 w-6",
          rating === 'positive'
            ? "text-green-600"
            : "text-muted-foreground"
        )}
        onClick={(e) => {
          e.stopPropagation()
          void handleRate('positive')
        }}
        disabled={isSubmitting}
        aria-label="Helpful"
        title="Helpful"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-6 w-6",
          rating === 'negative'
            ? "text-red-600"
            : "text-muted-foreground"
        )}
        onClick={(e) => {
          e.stopPropagation()
          void handleRate('negative')
        }}
        disabled={isSubmitting}
        aria-label="Not helpful"
        title="Not helpful"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}
